import { Vector3 } from "@babylonjs/core";
import type { JungleObstacle } from "../environment/JungleGenerator";

export interface GridCell { readonly x: number; readonly z: number; }

export interface NavigationGridOptions {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
  readonly cellSize: number;
  readonly clearance: number;
}

export class MovementProgressTracker {
  private anchor: Vector3;
  private elapsed = 0;
  constructor(position: Vector3, private readonly threshold = 0.08, private readonly timeoutSeconds = 2) { this.anchor = position.clone(); }
  update(position: Vector3, deltaSeconds: number): boolean {
    if (Vector3.DistanceSquared(position, this.anchor) > this.threshold ** 2) { this.anchor.copyFrom(position); this.elapsed = 0; return false; }
    this.elapsed += deltaSeconds;
    if (this.elapsed < this.timeoutSeconds) return false;
    this.anchor.copyFrom(position); this.elapsed = 0; return true;
  }
  reset(position: Vector3): void { this.anchor.copyFrom(position); this.elapsed = 0; }
}

const DEFAULT_OPTIONS: NavigationGridOptions = {
  minX: -28,
  maxX: 28,
  minZ: -28,
  maxZ: 28,
  cellSize: 1,
  clearance: 0.38,
};

/** A compact XZ navigation grid. Paths are calculated only on destination changes or recovery. */
export class NavigationGrid {
  readonly options: NavigationGridOptions;
  readonly width: number;
  readonly height: number;
  private readonly blocked: Uint8Array;

  constructor(obstacles: readonly JungleObstacle[], options: Partial<NavigationGridOptions> = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options };
    this.width = Math.floor((this.options.maxX - this.options.minX) / this.options.cellSize) + 1;
    this.height = Math.floor((this.options.maxZ - this.options.minZ) / this.options.cellSize) + 1;
    this.blocked = new Uint8Array(this.width * this.height);
    for (let z = 0; z < this.height; z++) {
      for (let x = 0; x < this.width; x++) {
        const point = this.cellToWorld({ x, z });
        if (obstacles.some((obstacle) => {
          const dx = point.x - obstacle.position.x;
          const dz = point.z - obstacle.position.z;
          return dx * dx + dz * dz <= (obstacle.radius + this.options.clearance) ** 2;
        })) this.blocked[this.index(x, z)] = 1;
      }
    }
  }

  isWalkableWorld(point: Vector3): boolean {
    const cell = this.worldToCell(point);
    return this.isWalkableCell(cell.x, cell.z);
  }

  findPath(startWorld: Vector3, goalWorld: Vector3): Vector3[] {
    const start = this.nearestWalkable(this.worldToCell(startWorld));
    const goal = this.nearestWalkable(this.worldToCell(goalWorld));
    if (!start || !goal) return [];
    const startIndex = this.index(start.x, start.z);
    const goalIndex = this.index(goal.x, goal.z);
    if (startIndex === goalIndex) return [goalWorld.clone()];

    const total = this.width * this.height;
    const gScore = new Float64Array(total); gScore.fill(Number.POSITIVE_INFINITY);
    const cameFrom = new Int32Array(total); cameFrom.fill(-1);
    const closed = new Uint8Array(total);
    const open = new Set<number>([startIndex]);
    gScore[startIndex] = 0;

    while (open.size > 0) {
      let current = -1;
      let best = Number.POSITIVE_INFINITY;
      for (const candidate of open) {
        const cell = this.cellFromIndex(candidate);
        const score = gScore[candidate]! + Math.hypot(cell.x - goal.x, cell.z - goal.z);
        if (score < best) { best = score; current = candidate; }
      }
      if (current === goalIndex) return this.reconstruct(cameFrom, current, goalWorld);
      open.delete(current); closed[current] = 1;
      const cell = this.cellFromIndex(current);
      for (const neighbour of this.neighbours(cell)) {
        const neighbourIndex = this.index(neighbour.x, neighbour.z);
        if (closed[neighbourIndex]) continue;
        const step = neighbour.x !== cell.x && neighbour.z !== cell.z ? Math.SQRT2 : 1;
        const tentative = gScore[current]! + step;
        if (tentative >= gScore[neighbourIndex]!) continue;
        cameFrom[neighbourIndex] = current;
        gScore[neighbourIndex] = tentative;
        open.add(neighbourIndex);
      }
    }
    return [];
  }

  randomWalkable(random: () => number = Math.random): Vector3 {
    for (let attempt = 0; attempt < 80; attempt++) {
      const x = Math.floor(random() * this.width);
      const z = Math.floor(random() * this.height);
      if (this.isWalkableCell(x, z)) return this.cellToWorld({ x, z });
    }
    return Vector3.Zero();
  }

  private reconstruct(cameFrom: Int32Array, end: number, exactGoal: Vector3): Vector3[] {
    const reversed: Vector3[] = [];
    let current = end;
    while (current >= 0) {
      reversed.push(this.cellToWorld(this.cellFromIndex(current)));
      current = cameFrom[current]!;
    }
    reversed.reverse();
    if (this.isWalkableWorld(exactGoal)) reversed[reversed.length - 1] = new Vector3(exactGoal.x, 0, exactGoal.z);
    return this.simplify(reversed);
  }

  private simplify(path: Vector3[]): Vector3[] {
    if (path.length < 3) return path;
    const simplified = [path[0]!];
    let previousDirection = "";
    for (let index = 1; index < path.length; index++) {
      const previous = path[index - 1]!;
      const current = path[index]!;
      const direction = `${Math.sign(current.x - previous.x)},${Math.sign(current.z - previous.z)}`;
      if (previousDirection && direction !== previousDirection) simplified.push(previous);
      previousDirection = direction;
    }
    simplified.push(path[path.length - 1]!);
    return simplified;
  }

  private neighbours(cell: GridCell): GridCell[] {
    const result: GridCell[] = [];
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      if (dx === 0 && dz === 0) continue;
      const x = cell.x + dx; const z = cell.z + dz;
      if (!this.isWalkableCell(x, z)) continue;
      if (dx !== 0 && dz !== 0 && (!this.isWalkableCell(cell.x + dx, cell.z) || !this.isWalkableCell(cell.x, cell.z + dz))) continue;
      result.push({ x, z });
    }
    return result;
  }

  private nearestWalkable(origin: GridCell): GridCell | undefined {
    if (this.isWalkableCell(origin.x, origin.z)) return origin;
    for (let radius = 1; radius <= 5; radius++) {
      for (let dz = -radius; dz <= radius; dz++) for (let dx = -radius; dx <= radius; dx++) {
        if (Math.abs(dx) !== radius && Math.abs(dz) !== radius) continue;
        const cell = { x: origin.x + dx, z: origin.z + dz };
        if (this.isWalkableCell(cell.x, cell.z)) return cell;
      }
    }
    return undefined;
  }

  private worldToCell(point: Vector3): GridCell {
    return {
      x: Math.round((point.x - this.options.minX) / this.options.cellSize),
      z: Math.round((point.z - this.options.minZ) / this.options.cellSize),
    };
  }

  private cellToWorld(cell: GridCell): Vector3 {
    return new Vector3(this.options.minX + cell.x * this.options.cellSize, 0, this.options.minZ + cell.z * this.options.cellSize);
  }

  private isWalkableCell(x: number, z: number): boolean {
    return x >= 0 && z >= 0 && x < this.width && z < this.height && this.blocked[this.index(x, z)] === 0;
  }
  private index(x: number, z: number): number { return z * this.width + x; }
  private cellFromIndex(index: number): GridCell { return { x: index % this.width, z: Math.floor(index / this.width) }; }
}
